// @flow

import _ from 'lodash';
import plans from 'src/constants/plans.json';
import plansPlus from 'src/constants/plans-plus.json';
import plansOld from 'src/constants/plans-old.json';
import plansOffer from 'src/constants/plans-offer.json';
import { nowTimestamp, getDayDiff, getUnixTimestamp } from 'src/helpers';
import moment from 'moment';
import { DATE_FORMAT } from 'src/constants/language-setting';
import { ADDONS_INFO, ADDONS_INFO_V4, ANALYTICS_ADDON, PACKAGES } from 'src/constants/subscription';
import {
  SPECIAL_PRICING_CHANGE_STORES,
  GENERAL_NOTIFY_PRICE_CHANGE_STORES,
  WHITE_GLOVE_TENANT_IDS,
  WHITE_GLOVE_SPECIAL_TENANT_IDS,
  GENERAL_SPECIAL_PRICING_CHANGE_STORE
} from 'src/constants/price-changes-stores';
import { RELEASE_0423_DAY } from 'src/constants/campaigns';
/**
 * Get subscription status
 *
 * @param  {string} selectedPlan   selected plan
 * @param  {boolean} trialExpired   is expired
 * @param  {number} trialExpiresAt  expires at
 *
 * @return {object}                { subscription status, maximum product for selected plan, day left if status is trial}
 */
export function getSubscriptionStatus(tenantApp: Object) {
  const {
    tenantId = '',
    selectedPlan = '',
    codeBFCMApplied,
    trialExpired,
    trialExpiresAt,
    exceededPlan = false,
    exceededPlanAt = 0,
    discountCode,
    chargeInfoActive = null,
    appliedDiscountCode,
    specialDiscountExpiredAt,
    specialDiscountExpired,
    allowToSubscribePlanV1
  } = tenantApp;

  let discountInfo = appliedDiscountCode || discountCode || {};
  discountInfo = {
    ...discountInfo,
    recentCodeApplied: codeBFCMApplied
  };

  const trialPackageDateStr = _.get(chargeInfoActive, 'trial_ends_on', null);
  let trialPackageDate = 0;
  if (trialPackageDateStr) {
    trialPackageDate = new Date(`${trialPackageDateStr}`).getTime() / 1000;
  }
  // Get total products
  const currentTotalProducts = _.get(tenantApp, 'appParams.currentTotalProducts', 0);
  const planName = _.get(tenantApp, 'externalInfo.plan_name', '');

  const codeApplied = _.get(discountCode, 'code', '');
  // Check apply special discount code
  const appliedSpecialDiscount = appliedSpecialDiscountCode(tenantId, discountInfo);

  const [todayDateString] = new Date().toISOString().split('T');
  const today = new Date(todayDateString).getTime() / 1000;

  const tomorrowDate = new Date();
  tomorrowDate.setHours(24, 0, 0);

  const amountDays = currentTotalProducts >= 50000 ? 15 : 7;
  const nextAmountDays = today + amountDays * 86400;

  let status = 'trial',
    dayLeft = 0,
    afterDayDiscountExpired;
  const now = nowTimestamp();

  const isShopifyPlus = _.get(tenantApp, 'externalInfo.plan_name', '') === 'shopify_plus';
  const allowToSubscribeRegularPlanV2 = _.get(tenantApp, 'allowToSubscribeRegularPlanV2', false);
  const allowToSubscribePlusPlanV2 = _.get(tenantApp, 'allowToSubscribePlusPlanV2', false);
  const subscribePlusPlanV2 =
    !allowToSubscribeRegularPlanV2 && (isShopifyPlus || allowToSubscribePlusPlanV2);

  const isExpiredRegularPlan = _.get(tenantApp, 'validateRegularPlanOffer.isExpired', false);
  const regularExpired = _.get(tenantApp, 'validateRegularPlanOffer.expiredAt', null);
  const appLanguage = _.get(tenantApp, 'appLanguage', 'en');

  const formatDateNumber = DATE_FORMAT[appLanguage].longDate;

  const regularExpiredAt = moment(new Date(regularExpired * 1e3)).format(formatDateNumber);

  if (
    isExpiredRegularPlan ||
    (regularExpired && regularExpired <= now && selectedPlan.includes('regular'))
  ) {
    status = 'regularExpired';
  } else if (exceededPlan) {
    status = 'exceeded';
  } else if (!exceededPlan && exceededPlanAt > 0 && getDayDiff(exceededPlanAt) < 15) {
    // Send notification for exceeded plan and exceededPlan will expired 15 day after
    status = 'exceededWithNotification';
  } else if (specialDiscountExpired) {
    // old special discount => flag = true and block app
    status = 'oldSpecialDiscountExpired';
  } else if (specialDiscountExpiredAt >= today && specialDiscountExpiredAt - today < 86400) {
    // new special discount => auto resubscribe and not block app
    status = 'specialDiscountExpired';
  } else if (
    // Expired after 1 - 7days
    specialDiscountExpiredAt &&
    specialDiscountExpiredAt <= today &&
    specialDiscountExpiredAt > today - 7 * 86400
  ) {
    afterDayDiscountExpired = 'specialDiscountAfterExpired';
    if (selectedPlan) {
      // status incase store upgrade from Free plan and have confirm subscription from shopify to have 14days trial
      if (today < trialPackageDate) {
        dayLeft = getDayDiff(trialPackageDate);
        status = 'trialPackage';
      } else {
        status = 'subscribed';
      }
    }
  } else if (
    specialDiscountExpiredAt &&
    specialDiscountExpiredAt > today &&
    codeApplied === 'Boost-BFCM21'
  ) {
    dayLeft = getDayDiff(specialDiscountExpiredAt);

    status = 'specialDiscount2021ExpiredSoon';
  } else if (
    selectedPlan.includes('regular') &&
    (isShopifyPlus || allowToSubscribePlusPlanV2) &&
    regularExpired > now &&
    getDayDiff(regularExpired) <= 15
  ) {
    dayLeft = getDayDiff(regularExpired);
    status = 'regularExpiredSoon';
  } else if (
    appliedSpecialDiscount &&
    specialDiscountExpiredAt &&
    specialDiscountExpiredAt > today &&
    specialDiscountExpiredAt <= nextAmountDays
  ) {
    dayLeft = Math.abs(getDayDiff(specialDiscountExpiredAt));
    status = 'specialDiscountExpiredSoon';
  } else if (selectedPlan) {
    // status incase store upgrade from Free plan and have confirm subscription from shopify to have 14days trial
    if (today < trialPackageDate) {
      dayLeft = getDayDiff(trialPackageDate);
      status = 'trialPackage';
    } else {
      status = 'subscribed';
    }
  } else if (trialExpired) {
    status = 'expired';
  } else if (trialExpiresAt > now) {
    dayLeft = getDayDiff(trialExpiresAt);
    if (dayLeft <= 2) {
      status = 'expiredSoon';
    } else {
      status = 'trial';
    }
  }

  // Get correct plan based on total product
  const correctPlan = getCorrectPlanV1(
    currentTotalProducts,
    subscribePlusPlanV2,
    allowToSubscribePlanV1
  );

  // Exceeded with notifications' first 7 days flag
  let isFirst7Days = false;
  if (status === 'exceededWithNotification') {
    const [todayDateString] = new Date().toISOString().split('T');
    const today = new Date(todayDateString).getTime() / 1000;

    isFirst7Days = exceededPlanAt >= today - 7 * 86400;
  }
  // Condition disable CTA button subscribe/add add-on
  // - Store trial + store with plan test
  const isTrialPeriodStoreTest =
    ['trial', 'expiredSoon'].includes(status) && checkShopifyTestPlan(planName, tenantId);

  // - store in expired periods
  const storeStatusExpired = [
    'expired',
    'exceeded',
    'regularExpired',
    'specialDiscountExpired'
  ].includes(status);

  // - store expired today but cronjob still not update expired = true => current status till = trial/expiredSoon - exclude store dev
  const storeTrialExpired =
    ['trial', 'expiredSoon'].includes(status) &&
    //    + check expiredAt < now
    getDayDiff(trialExpiresAt) < 0 &&
    !tenantId?.includes('bc-dev-v2');

  const notAllowAddon = isTrialPeriodStoreTest || storeStatusExpired || storeTrialExpired;

  const boltOn = _.get(tenantApp, 'boltOn', {});
  const useBoltOn = boltOn.status === 'active';

  return {
    status,
    billingOn: _.get(chargeInfoActive, 'trial_ends_on', ''),
    dayLeft,
    trialExpiresAt: moment(trialExpiresAt * 1000).format(DATE_FORMAT[appLanguage].longDate),
    regularExpiredAt,
    correctPlan,
    afterDayDiscountExpired,
    currentTotalProducts,
    isFirst7Days,
    discountCode: discountInfo,
    notAllowAddon,
    useBoltOn
  };
}

/**
 * Get minimum plan base on current total product of the store
 *
 * @param  {number} totalProducts current total product
 * @param  {boolean} isPlusPlan
 *
 * @return {object}               minimum plan
 */
export function getCorrectPlan(chargeInfo, packagePlans, currentTotalProducts) {
  // Return basic plan if trial
  if (_.isEmpty(chargeInfo)) {
    return _.get(packagePlans, 'basic', []).find(e => e.products > currentTotalProducts);
  }
  const chargeInfoActiveName = _.get(chargeInfo, 'name', '');
  if (!chargeInfoActiveName) {
    return _.get(packagePlans, 'basic', []).find(e => e.products > currentTotalProducts);
  }
  const correctPlanName = chargeInfoActiveName ? chargeInfoActiveName.replace('_discount', '') : '';
  const chargeInfoActiveNameSplit = chargeInfoActiveName.split('_');

  // Check if store subscribed subscription v2
  if (
    !['basic', 'essential', 'professional'].includes(_.get(chargeInfoActiveNameSplit, '[0]', ''))
  ) {
    let currentPlanProducts = currentTotalProducts;
    if (correctPlanName.includes('plan')) {
      const correctOldPlan = plansOld[correctPlanName];
      return {
        ...correctOldPlan,
        name: 'basic_' + correctOldPlan?.name
      };
    } else if (correctPlanName.includes('regular'))
      currentPlanProducts = plans[correctPlanName]?.products;
    else if (correctPlanName.includes('plus'))
      currentPlanProducts = plansPlus[correctPlanName]?.products || 0;
    else if (correctPlanName.includes('offer'))
      currentPlanProducts = plansOffer[correctPlanName]?.products;

    return _.get(packagePlans, 'basic', []).find(e => e?.products >= currentPlanProducts);
  }

  const originPlan = _.get(packagePlans, chargeInfoActiveNameSplit[0], []).find(
    e => e.name === correctPlanName
  );

  const correctPlan = {
    ...chargeInfo,
    ...originPlan
  };

  return correctPlan;
}

/**
 * Get minimum plan base on current total product of the store
 *
 * @param  {number} totalProducts current total product
 * @param  {boolean} isPlusPlan
 *
 * @return {object}               minimum plan
 */
export function getCorrectPlanV4(chargeInfo, packagePlans, currentTotalProducts, selectedPlan) {
  // Return basic plan if trial
  if (_.isEmpty(chargeInfo)) {
    if (selectedPlan.includes('freemium')) {
      return _.get(packagePlans, 'freemium[0]', {
        name: 'freemium_regular',
        price: 0,
        products: 25
      });
    }
    return _.get(packagePlans, 'basic', []).find(e => e.products > currentTotalProducts);
  }
  const chargeInfoActiveName = _.get(chargeInfo, 'name', '');
  if (!chargeInfoActiveName) {
    return _.get(packagePlans, 'basic', []).find(e => e.products > currentTotalProducts);
  }
  const correctPlanName = chargeInfoActiveName ? chargeInfoActiveName.replace('_discount', '') : '';
  const chargeInfoActiveNameSplit = chargeInfoActiveName.split('_');

  const originPlan = _.get(packagePlans, chargeInfoActiveNameSplit[0], []).find(
    e => e.name === correctPlanName
  );

  const correctPlan = {
    ...chargeInfo,
    ...originPlan
  };

  return correctPlan;
}

/**
 * Get minimum plan base on current total product of the store
 *
 * @param  {number} totalProducts current total product
 * @param  {boolean} isPlusPlan
 *
 * @return {object}               minimum plan
 */
function getCorrectPlanV1(
  totalProducts: number,
  isPlusPlan: boolean,
  allowToSubscribePlanV1: boolean = false
) {
  const plansList = allowToSubscribePlanV1 ? plansOld : isPlusPlan ? plansPlus : plans;
  // Get selected plan
  let plan = _.find(plansList, item => {
    return item.products >= totalProducts;
  });

  if (!plan) {
    const keys = Object.keys(plansList);
    plan = plansList[_.last(keys)];
  }

  return plan;
}

/**
 * Get new corrected plan based on planId - use to subscribe new plan
 *
 * @param  {object} correctPlan plan store can apply
 * @param  {object} chargeInfoActive plan store applied
 * @return {object}        plan info
 */
export function getSubscribePlanInfo(correctPlan: object, invoice: object) {
  const chargeInfo = _.get(invoice, 'chargeInfo', {});
  if (invoice && !_.isEmpty(invoice) && !_.isEmpty(chargeInfo)) {
    const {
      subscribedAddOnsInfos = [],
      discountInfo = {},
      discountCodeApplied = {},
      totalPrice = 0,
      totalPriceAfterDiscount = 0,
      extraFee = 0,
      isExtraFeeCharged = false
    } = invoice;
    return {
      name: _.get(correctPlan, 'name', ''),
      activatedOn: chargeInfo.activatedOn,
      products: _.get(correctPlan, 'products', 0),
      price: parseInt(chargeInfo.price),
      addon: subscribedAddOnsInfos,
      originalPrice: parseFloat(totalPrice),
      totalPrice: totalPriceAfterDiscount,
      amountDiscount: parseFloat(totalPrice) - parseFloat(totalPriceAfterDiscount),
      discountInfo,
      discountCodeApplied,
      extraFee,
      isExtraFeeCharged
    };
  }
  const totalPrice = _.get(invoice, 'totalPrice', 0) + _.get(correctPlan, 'price', 0);
  return {
    name: _.get(correctPlan, 'name', ''),
    price: _.get(correctPlan, 'price', 0),
    addon: _.get(invoice, 'subscribedAddOnsInfos', []),
    products: _.get(correctPlan, 'products', 0),
    totalPrice,
    originalPrice: totalPrice,
    extraFee: _.get(invoice, 'extraFee', 0),
    isExtraFeeCharged: _.get(invoice, 'isExtraFeeCharged', false)
  };
}

/**
 * Check if the store applied special discount code
 *
 * @param  {string} tenantId
 * @param  {string} selectedPlan
 * @param  {string} discountCode
 *
 * @return {boolean}              true | false
 */
function appliedSpecialDiscountCode(tenantId: string, discountCode: Object) {
  if (!discountCode || _.isEmpty(discountCode)) return false;

  const code = _.get(discountCode, 'code', '');
  const tenant = tenantId.replace(/.myshopify.com/g, '');

  const specialDiscountCodes = [`STAYSTRONG-${tenant}`, `BOOSTOFFER-${tenant}`];

  // Has apply special discount code with prefix
  const hasAppliedCodeWithPrefix = [
    'BFCM',
    'VETERAN22',
    'XMAS22',
    'BOOSTAI23',
    'ANALYTIC23',
    'BOOSTAI15',
    'BOOSTAI20'
  ].find(element => code.includes(element));
  return _.includes(specialDiscountCodes, code) || hasAppliedCodeWithPrefix;
}

/**
 * Check discount expired from n days
 * @param {string} status
 * @param {number} expiredAt
 * @param {number} days
 * @returns
 */
export function isSpecialDiscount2021Expired(status: string, expiredAt: number): boolean {
  const today = nowTimestamp();

  const isSpecialDiscountExpired =
    status === 'specialDiscountExpired' && ((expiredAt && expiredAt <= today) || !expiredAt);

  return isSpecialDiscountExpired;
}

/**
 * Allow to subscribe special discount code BFCM20
 *
 * @param  {object} tenantApp
 * @param  {string} subscribeStatus
 *
 * @return {boolean}
 */
export const allowToSubscribeBFCM20Code = (tenantApp: Object) => {
  const selectedPlan = _.get(tenantApp, 'selectedPlan', '');
  const planName = _.get(tenantApp, 'externalInfo.plan_name', '');
  const allowToSubscribePlanV1 = _.get(tenantApp, 'allowToSubscribePlanV1', false);
  const now = nowTimestamp();
  const deadline = 1606867199; // Dec 01, 2020

  return now <= deadline &&
    planName === 'shopify_plus' &&
    _.startsWith(selectedPlan, 'plan') &&
    !allowToSubscribePlanV1
    ? true
    : false;
};

/**
 * Allow to subscribe special discount code for BFCM 2022
 * Trial user
 * - Current trial
 * - Expired
 * - Not paid or uninstalled
 * @param {*} tenantApp
 * @param {*} subscribeStatus
 * @returns
 */
export const allowToSubscribeBFCMCode = (tenantApp: Object): boolean => {
  const { notChargedBefore = false, selectedPlan = '', allowToSubscribePlanV1 = false } = tenantApp;
  const now = nowTimestamp();
  const startDate = getUnixTimestamp(new Date('00:00 AM, Oct 05, 2022')); // 00:00 AM, Oct 05, 2022
  const endDate = getUnixTimestamp(new Date('00:00 AM, Nov 06, 2022')); // 00:00 AM, Nov 06, 2022
  return (
    now >= startDate &&
    now <= endDate &&
    selectedPlan === '' &&
    notChargedBefore &&
    !allowToSubscribePlanV1
  );
};

// Check if Shopify plan is a paid plan
export const checkShopifyPaidPlan = (planName: string): boolean => {
  return ![
    'affiliate',
    'cancelled',
    'staff',
    'closed',
    'plus_partner_sandbox',
    'trial',
    'frozen',
    'paused',
    'fraudulent',
    'partner_test',
    'dormant'
  ].includes(planName);
};

/**
 * format price with format xx.yy
 * @param {*} num
 * @returns
 */
export const formatPriceDisplay = (num: number = 0): number => {
  return num?.toFixed(2) || num;
};

/**
 * Render billing monthly to display in review package based on correctPlan
 * @param {object} correctPlan
 * @param {object} discount
 * @param {object} addonAdded
 * @param {object} analyticValue
 * @returns
 */
export const calculateBillingMonthly = (
  correctPlan,
  discount,
  addonAdded,
  analyticValue,
  remainCycles
) => {
  const { price: packagePrice = 0, name: packageName = '' } = correctPlan;
  const { discountType = 'percentage', addOns = [], scope = 'global' } = discount;
  const { dcRawValue: discountValue } = getDiscountCodeValue(discount);

  // Push discount package amount to discountDetail
  const discountDetail = [];
  if (['global', 'package'].includes(scope) && discountType === 'percentage') {
    discountDetail.push({
      name: `AdminHTML.DashboardV3.SubscriptionStatus.packages.${packageName.split('_')[0]}`,
      price: calculateDiscountAmount(discountType, discountValue, packagePrice)
    });
  }

  // Calculate discount addon amount and not discount addon amount
  let notDiscountAddonTotal = 0;
  let discountAddonTotal = 0;
  addonAdded.forEach(({ price: priceAddon = 0, name = '', addonName = '', title = '' }) => {
    const addOnId = name === 'analytics' ? analyticValue.name.replace('-days', '') : addonName;
    const addonPrice = name === 'analytics' && analyticValue ? analyticValue?.price : priceAddon;

    // Discount add on
    if ((addOns.includes(addOnId) || addOns.length === 0) && ['global', 'add-on'].includes(scope)) {
      discountAddonTotal += addonPrice;

      // If percentate
      if (discountType === 'percentage') {
        const addOnTitle = name === 'analytics' ? analyticValue.label : title;
        const price = calculateDiscountAmount(discountType, discountValue, addonPrice);
        discountDetail.push({ name: addOnTitle, price });
      }
    }
    // Not discount addon
    else {
      notDiscountAddonTotal += addonPrice;
    }
  });

  // Calculate total discount amount
  let discountAmount = 0;
  if (discountValue && scope) {
    let originPrice = 0;
    if (scope === 'global') originPrice = packagePrice + discountAddonTotal;
    else if (scope === 'package') originPrice = packagePrice;
    else if (scope === 'add-on') originPrice = discountAddonTotal;

    discountAmount = calculateDiscountAmount(discountType, discountValue, originPrice);
  }

  // Set expired date
  let expiredDate = '';
  if (remainCycles) {
    const expiredAt = new Date().setDate(new Date().getDate() + remainCycles * 30);
    expiredDate = moment(expiredAt).format(DATE_FORMAT['en'].longDate);
  }

  const originTotal = packagePrice + notDiscountAddonTotal + discountAddonTotal;

  return {
    discountValue,
    discountType,
    remainCycles,
    total: originTotal - discountAmount,
    originTotal,
    discountAmount,
    expiredDate,
    discountDetail
  };
};

/**
 * Get discount amount
 * @param {*} discountType
 * @param {*} discountValue
 * @param {*} originPrice
 * @returns
 */
export const calculateDiscountAmount = (
  discountType = 'percentage',
  discountValue,
  originPrice
) => {
  if (discountType === 'percentage') return originPrice * discountValue;
  return Math.min(discountValue, originPrice);
};

/**
 * Get discount value by type
 * @param {*} discountType
 * @param {*} discountValue
 * @param {*} percent
 * @returns
 */
export const getDiscountCodeValue = discountCode => {
  const percent = _.get(discountCode, 'percent', 0);
  const discountType = _.get(discountCode, 'discountType', 'percentage');
  const discountValue = _.get(discountCode, 'discountValue', 0);

  const dcRawValue = discountValue || percent;
  const dcValue = discountType === 'percentage' ? dcRawValue * 100 : dcRawValue;
  const dcPostfixValue = discountType === 'percentage' ? `${dcValue}%` : `${dcValue}$`;

  return { dcRawValue, dcValue, dcPostfixValue };
};

/**
 * Build list addon with current status trial | subscribe | added
 * @param {object} allAddonWithStatus Addon list from DB
 */
export const buildListAddon = (
  allAddonWithStatus: object,
  status: string,
  useFreeVariantDisplay: boolean = false
  // numberOfProducts: number
) => {
  let addonConstant = [...ADDONS_INFO];
  const {
    unsubscribedAddOns = [],
    addOnTrials = [],
    subscribedAddOns = [],
    downloads = {}
  } = allAddonWithStatus;
  const unsubscribedAddOnsName = unsubscribedAddOns.map(e => e.name);
  const hasSubscribedLargeAnalytic = subscribedAddOns.find(e => e.name === 'analytics-365-days');
  const subscribedAnalytic = subscribedAddOns.find(e => _.includes(e.name, 'analytics'));

  if (useFreeVariantDisplay) {
    addonConstant = _.filter(addonConstant, e => e.addonName !== 'variant-display');
  }

  // Build list addon with 3 status
  //  added: addon subscribed
  //  addToTrial: addon is allowed trial
  //  subscribeNow: addon will be paying
  const responseList = addonConstant.map(addon => {
    const { addonName = '', name } = addon;

    // Check if addonName is analytics and subscribed analytics-365-days
    if (addonName === 'analytics' && hasSubscribedLargeAnalytic)
      return { ...addon, status: 'added', addonName: subscribedAnalytic.name };

    // If addon not subscribed / add to trial
    if (unsubscribedAddOnsName.includes(addonName) || addonName === 'analytics') {
      let statusAddon = 'addToTrial';
      let isTrialPeriod = true;
      const isTrialStatus = ['trial', 'expiredSoon'].includes(status);

      const findAddon = addOnTrials.find(e => e.addOnName === addonName);
      if (findAddon) {
        const addonRemainingCycles = _.get(findAddon, 'remainingDays', 0);
        isTrialPeriod = addonRemainingCycles > 0;
      }

      // If store subscribed + ( addon end trial period || addon is analytic )
      // Note: if store trial       => all addon is allowed trial
      //       if store subscribed  => addon analytic is not allowed trial
      if (
        !isTrialStatus &&
        (!isTrialPeriod || addonName === 'analytics')
        // // addon variant display and old store has <= 5000 products and subscribed package v3
        // (isSubscribedPackageV3 &&
        //   addonName === 'variant-display' &&
        //   numberOfProducts <= 5000 &&
        //   userAppVersion !== 'v3')
        // addon variant display and old store and subscribed package v3
        // (isSubscribedPackageV3 && addonName === 'variant-display' && userAppVersion !== 'v3')
      ) {
        statusAddon = 'subscribeNow';
      }
      return {
        ...addon,
        status: statusAddon,
        downloads: downloads[name],
        addonName:
          addonName === 'analytics' && subscribedAnalytic ? subscribedAnalytic.name : addonName
      };
    }
    // addon => added
    return { ...addon, status: 'added', downloads: downloads[name] };
  });
  return responseList;
};

/**
 * Build list addon with current status trial | subscribe | added
 * @param {object} allAddonWithStatus Addon list from DB
 */
export const buildListAddonV4 = (
  allAddonWithStatus: object,
  status: string,
  useFreeVariantDisplay: boolean = false
  // numberOfProducts: number
) => {
  let addonConstant = [...ADDONS_INFO_V4];
  const { unsubscribedAddOns = [], addOnTrials = [], downloads = {} } = allAddonWithStatus;
  const unsubscribedAddOnsName = unsubscribedAddOns.map(e => e.name);

  if (useFreeVariantDisplay) {
    addonConstant = _.filter(addonConstant, e => e.addonName !== 'variant-display');
  }

  // Build list addon with 3 status
  //  added: addon subscribed
  //  addToTrial: addon is allowed trial
  //  subscribeNow: addon will be paying
  const responseList = addonConstant.map(addon => {
    const { addonName = '', name } = addon;
    // If addon not subscribed / add to trial
    if (unsubscribedAddOnsName.includes(addonName)) {
      let statusAddon = 'addToTrial';
      let isTrialPeriod = true;
      const isTrialStatus = ['trial', 'expiredSoon'].includes(status);

      const findAddon = addOnTrials.find(e => e.addOnName === addonName);
      if (findAddon) {
        const addonRemainingCycles = _.get(findAddon, 'remainingDays', 0);
        isTrialPeriod = addonRemainingCycles > 0;
      }

      // If store subscribed + ( addon end trial period || addon is analytic )
      // Note: if store trial       => all addon is allowed trial
      //       if store subscribed  => addon analytic is not allowed trial
      if (!isTrialStatus && !isTrialPeriod) {
        statusAddon = 'subscribeNow';
      }
      return {
        ...addon,
        status: statusAddon,
        downloads: downloads[name],
        addonName
      };
    }
    // addon => added
    return { ...addon, status: 'added', downloads: downloads[name] };
  });
  return responseList;
};
/**
 * Build analytics available can subscribe
 * @param {object} addon
 * @param {object} currentPackage
 * @returns
 */
export const buildAnalyticsInstance = (addon, currentPackage) => {
  const { chargeInfo = {} } = currentPackage;
  let analyticList = [...ANALYTICS_ADDON];
  // Get current package => basic | essential | professional
  const namePackage = !_.isEmpty(chargeInfo) ? _.get(chargeInfo, 'name', 'basic') : 'basic';
  let valueAnalyticSubscribed = null;

  // get value analytics => 90 | 180 | 365
  if (addon.addonName !== 'analytics' && addon.addonName.includes('analytics')) {
    // incase: addon analytic in list addon subscribed
    const splitValue = addon.addonName.split('-');
    valueAnalyticSubscribed = splitValue[1];
  } else {
    // incase analytic included package
    let currentPackage = PACKAGES.find(e => _.includes(namePackage, e.name));
    if (!currentPackage) {
      currentPackage = PACKAGES.find(e => _.includes('basic', e.name));
    }
    const findAnalytics = currentPackage.addons.find(e => _.includes(e, 'analytics'));
    const splitValue = findAnalytics.split('-');
    valueAnalyticSubscribed = splitValue[0];
  }
  // get addon analytic info by valueAnalyticSubscribed
  const numberOfDayAnalytics = parseInt(valueAnalyticSubscribed);
  analyticList = ANALYTICS_ADDON.filter(e => parseInt(e.value) > numberOfDayAnalytics);
  return analyticList.length > 0 ? analyticList : ANALYTICS_ADDON.slice(-1);
};

/**
 * Build invoice discount in plan status
 * @param {*} chargeInfo
 * @param {*} currentDiscountApplied
 * @param {*} currentPackage
 * @param {*} i18n
 * @returns
 */
export const buildDiscountInvoice = (
  planInfo,
  packageInfo = {},
  currentDiscountApplied,
  i18n
) => {
  const { price = 0, amountDiscount = 0 } = planInfo;
  const scope = _.get(currentDiscountApplied, 'scope', '');
  const discountType = _.get(currentDiscountApplied, 'discountType', 'percentage');
  const { dcRawValue: discountValue } = getDiscountCodeValue(currentDiscountApplied);
  const hasVerifiedDiscount =
    !_.isEmpty(currentDiscountApplied) &&
    !!_.get(currentDiscountApplied, 'code', '') &&
    discountValue > 0;

  const discountInvoiceInfos = _.get(planInfo, 'discountInfo', {});

  const packageDiscountInfo = _.get(discountInvoiceInfos, 'package', {});
  const extraFee = Number(_.get(planInfo, 'extraFee', 0));
  const isExtraFeeCharged = _.get(planInfo, 'isExtraFeeCharged', false);
  const packagePrice = price + (isExtraFeeCharged ? extraFee : 0);
  const currentPackage = {
    name: _.get(packageInfo, 'title', null)
      ? `${i18n.translate(packageInfo?.title)} ${i18n.translate(
          'AdminHTML.SubscriptionV3.Common.package'
        )}`
      : _.get(planInfo, 'packageName', ''),
    price: packagePrice,
    priceAfterDiscount: !hasVerifiedDiscount && !_.isEmpty(packageDiscountInfo)
      ? packageDiscountInfo.discountPrice
      : discountValue && scope !== 'add-on'
        ? calculateDiscountAmount(discountType, discountValue, price)
        : price
  };
  const addonList = _.get(planInfo, 'addon', []);
  const totalPrice = Number(_.get(planInfo, 'totalPrice', 0)) + (isExtraFeeCharged ? extraFee : 0);
  // // Map addon list value to display
  const addons = addonList.map(addonSubscribe => {
    const { addOnName = '', price = 0 } = addonSubscribe;
    if (_.includes(addOnName, 'analytics')) {
      const analyticValue = ANALYTICS_ADDON.find(e => e.name === addOnName);
      return {
        name:
          analyticValue && analyticValue.label ? i18n.translate(analyticValue.label) : addOnName,
        price: _.get(analyticValue, 'price', 0)
      };
    }

    const findAddon = ADDONS_INFO.find(e => e.addonName === addOnName);
    if (findAddon)
      return {
        name: findAddon ? i18n.translate(findAddon.title) : '',
        price
      };
    return null;
  });
  // Keep invoice-applied discount by default, but prefer the freshly verified
  // discount data when user is applying a new code in the modal.
  if (!_.isEmpty(discountInvoiceInfos) && !hasVerifiedDiscount) {
    const discountCodeApplied = _.get(planInfo, 'discountCodeApplied', {});
    const appliedScope = _.get(discountCodeApplied, 'scope', '');
    const expiredAt = _.get(discountInvoiceInfos, 'expiredAt', 0);
    const originalPrice = _.get(planInfo, 'originalPrice', totalPrice + amountDiscount);
    const canShowAddonDiscounts = ['global', 'add-on'].includes(appliedScope);
    const invoiceAddonsWithDiscount = canShowAddonDiscounts
      ? _.get(discountInvoiceInfos, 'addOns', [])
      : [];
    const discountInfoSubscribed = {
      discountValue: getDiscountCodeValue(discountCodeApplied).dcRawValue,
      discountType: _.get(discountCodeApplied, 'discountType', 'percentage'),
      duration: _.get(discountCodeApplied, 'duration', 0),
      expiredAt: expiredAt ? moment(expiredAt * 1000).format(DATE_FORMAT['en'].longDate) : '',
      code: _.get(discountCodeApplied, 'code', '')
    };
    const shouldBuildAddonDiscounts =
      _.isEmpty(invoiceAddonsWithDiscount) &&
      ['global', 'add-on'].includes(appliedScope) &&
      discountInfoSubscribed.discountValue > 0;
    const addonsWithDiscount = shouldBuildAddonDiscounts
      ? addonList.map(element => {
          const discountPrice = calculateDiscountAmount(
            discountInfoSubscribed.discountType,
            discountInfoSubscribed.discountValue,
            _.get(element, 'price', 0)
          );
          return {
            name: _.get(element, 'addOnName', null),
            discountPrice,
            priceAfterDiscount: _.get(element, 'price', 0) - discountPrice
          };
        })
      : invoiceAddonsWithDiscount;
    const packageAmountDiscount =
      appliedScope === 'add-on' ? 0 : _.get(packageDiscountInfo, 'discountPrice', 0);
    const calculatedAmountDiscount = shouldBuildAddonDiscounts
      ? packageAmountDiscount +
        addonsWithDiscount.reduce(
          (totalDiscount, addon) => totalDiscount + _.get(addon, 'discountPrice', 0),
          0
        )
      : amountDiscount;
    const calculatedTotalPrice = shouldBuildAddonDiscounts
      ? originalPrice -
        calculatedAmountDiscount +
        (_.get(planInfo, 'isExtraFeeCharged', false) ? _.get(planInfo, 'extraFee', 0) : 0)
      : totalPrice;

    return {
      addonsWithDiscount,
      discountInfo: discountInfoSubscribed,
      addons,
      packageWithDiscount:
        appliedScope === 'add-on' ? null : _.get(discountInvoiceInfos, 'package', {}),
      totalPrice: calculatedTotalPrice,
      originalPrice,
      currentPackage,
      amountDiscount: calculatedAmountDiscount
    };
  }

  const chargeInfo = {
    addons,
    totalPrice,
    originalPrice: _.get(planInfo, 'originalPrice', 0),
    currentPackage,
    amountDiscount
  };
  // In case store is trial period and verifying package/addon with discount
  if (!_.isEmpty(currentDiscountApplied)) {
    const expiredDate = new Date().setDate(
      new Date().getDate() + _.get(currentDiscountApplied, 'duration', 0) * 30
    );

    let discountInfo = {
      discountValue,
      discountType,
      duration: _.get(currentDiscountApplied, 'duration', 0),
      expiredAt: moment(expiredDate).format(DATE_FORMAT['en'].longDate),
      code: _.get(currentDiscountApplied, 'code', '')
    };

    let addonsWithDiscount = [];

    let amount = amountDiscount;
    switch (scope) {
      case 'global': {
        addonsWithDiscount = addonList.map(element => {
          const discountPrice = calculateDiscountAmount(
            discountType,
            discountInfo.discountValue,
            _.get(element, 'price', 0)
          );
          return {
            name: _.get(element, 'addOnName', null),
            discountPrice,
            priceAfterDiscount: _.get(element, 'price', 0) - discountPrice
          };
        });
        // Scope = global  => discount total = calculateDiscountAmount(originalPrice)
        amount = calculateDiscountAmount(
          discountType,
          discountInfo.discountValue,
          _.get(planInfo, 'originalPrice', 0)
        );
        break;
      }
      case 'add-on': {
        addonsWithDiscount = addonList.map(element => {
          const discountPrice = calculateDiscountAmount(
            discountType,
            discountInfo.discountValue,
            _.get(element, 'price', 0)
          );
          return {
            name: _.get(element, 'addOnName', null),
            discountPrice,
            priceAfterDiscount: _.get(element, 'price', 0) - discountPrice
          };
        });
        // Total global discount (package + addon) - price after discount of package = total discount addon
        const currentDiscountValue = _.get(currentDiscountApplied, 'discountValue', 0);
        amount =
          discountType === 'percentage'
            ? _.get(planInfo, 'originalPrice', 0) * discountInfo.discountValue -
              price * currentDiscountValue
            : Math.min(price, currentDiscountValue);
        break;
      }
      default:
        amount = currentPackage.priceAfterDiscount;
    }

    if (scope === 'add-on' && !_.get(addonsWithDiscount, 'length', 0)) {
      amount = 0;
      discountInfo = [];
    }
    return {
      ...chargeInfo,
      addonsWithDiscount,
      discountInfo,
      packageWithDiscount: scope !== 'add-on' ? currentPackage : null,
      amountDiscount: amount,
      totalPrice:
        _.get(planInfo, 'originalPrice', 0) -
        amount +
        (_.get(planInfo, 'isExtraFeeCharged', false) ? _.get(planInfo, 'extraFee', 0) : 0)
    };
  }

  return chargeInfo;
};

/**
 * Build invoice discount in plan status
 * @param {*} chargeInfo
 * @param {*} currentDiscountApplied
 * @param {*} currentPackage
 * @param {*} i18n
 * @returns
 */
export const buildDiscountInvoiceV4 = (
  planInfo,
  packageInfo = {},
  currentDiscountApplied,
  i18n
) => {
  const { price = 0, amountDiscount = 0 } = planInfo;
  const scope = _.get(currentDiscountApplied, 'scope', '');
  const discountType = _.get(currentDiscountApplied, 'discountType', 'percentage');
  const { dcRawValue: discountValue } = getDiscountCodeValue(currentDiscountApplied);

  const discountInvoiceInfos = _.get(planInfo, 'discountInfo', {});

  const packageDiscountInfo = _.get(discountInvoiceInfos, 'package', {});
  const currentPackage = {
    name: _.get(packageInfo, 'title', null)
      ? `${i18n.translate(packageInfo?.title)} ${i18n.translate(
          'AdminHTML.SubscriptionV4.Common.plan'
        )}`
      : '',
    price: price,
    priceAfterDiscount: !_.isEmpty(packageDiscountInfo)
      ? packageDiscountInfo.discountPrice
      : discountValue && scope !== 'add-on'
        ? calculateDiscountAmount(discountType, discountValue, price)
        : price
  };
  const addonList = _.get(planInfo, 'addon', []);
  // // Map addon list value to display
  const addons = addonList.map(addonSubscribe => {
    const { addOnName = '', price = 0 } = addonSubscribe;
    const findAddon = ADDONS_INFO_V4.find(e => e.addonName === addOnName);
    if (findAddon)
      return {
        name: findAddon ? i18n.translate(findAddon.title) : '',
        price
      };
    return null;
  });
  // In case store is already subscribe package/addon with discount
  if (!_.isEmpty(discountInvoiceInfos)) {
    const discountCodeApplied = _.get(planInfo, 'discountCodeApplied', {});
    const expiredAt = _.get(discountInvoiceInfos, 'expiredAt', 0);
    const discountInfoSubscribed = {
      discountValue: getDiscountCodeValue(discountCodeApplied).dcRawValue,
      discountType: _.get(discountCodeApplied, 'discountType', 'percentage'),
      duration: _.get(discountCodeApplied, 'duration', 0),
      expiredAt: expiredAt ? moment(expiredAt * 1000).format(DATE_FORMAT['en'].longDate) : '',
      code: _.get(discountCodeApplied, 'code', '')
    };
    return {
      addonsWithDiscount: _.get(discountInvoiceInfos, 'addOns', []),
      discountInfo: discountInfoSubscribed,
      addons,
      packageWithDiscount: _.get(discountInvoiceInfos, 'package', {}),
      totalPrice: _.get(planInfo, 'totalPrice', 0),
      originalPrice: _.get(planInfo, 'originalPrice', 0),
      currentPackage,
      amountDiscount
    };
  }
  let totalPrice = _.get(planInfo, 'totalPrice', 0);

  const chargeInfo = {
    addons,
    totalPrice,
    originalPrice: _.get(planInfo, 'originalPrice', 0),
    currentPackage,
    amountDiscount
  };
  // In case store is trial period and verifying package/addon with discount
  if (!_.isEmpty(currentDiscountApplied)) {
    const expiredDate = new Date().setDate(
      new Date().getDate() + _.get(currentDiscountApplied, 'duration', 0) * 30
    );

    let discountInfo = {
      discountValue,
      discountType,
      duration: _.get(currentDiscountApplied, 'duration', 0),
      expiredAt: moment(expiredDate).format(DATE_FORMAT['en'].longDate),
      code: _.get(currentDiscountApplied, 'code', '')
    };

    let addonsWithDiscount = [];

    let amount = amountDiscount;
    switch (scope) {
      case 'global': {
        addonsWithDiscount = addonList.map(element => {
          const discountPrice = calculateDiscountAmount(
            discountType,
            discountInfo.discountValue,
            _.get(element, 'price', 0)
          );
          return {
            name: _.get(element, 'addOnName', null),
            discountPrice,
            priceAfterDiscount: _.get(element, 'price', 0) - discountPrice
          };
        });
        // Scope = global  => discount total = calculateDiscountAmount(originalPrice)
        amount = calculateDiscountAmount(
          discountType,
          discountInfo.discountValue,
          _.get(planInfo, 'originalPrice', 0)
        );
        break;
      }
      case 'add-on': {
        addonsWithDiscount = addonList.map(element => {
          const discountPrice = calculateDiscountAmount(
            discountType,
            discountInfo.discountValue,
            _.get(element, 'price', 0)
          );
          return {
            name: _.get(element, 'addOnName', null),
            discountPrice,
            priceAfterDiscount: _.get(element, 'price', 0) - discountPrice
          };
        });
        // Total global discount (package + addon) - price after discount of package = total discount addon
        const currentDiscountValue = _.get(currentDiscountApplied, 'discountValue', 0);
        amount =
          discountType === 'percentage'
            ? _.get(planInfo, 'originalPrice', 0) * discountInfo.discountValue -
              price * currentDiscountValue
            : Math.min(price, currentDiscountValue);
        break;
      }
      default:
        amount = currentPackage.priceAfterDiscount;
    }

    if (scope === 'add-on' && !_.get(addonsWithDiscount, 'length', 0)) {
      amount = 0;
      discountInfo = [];
    }
    return {
      ...chargeInfo,
      addonsWithDiscount,
      discountInfo,
      packageWithDiscount: scope !== 'add-on' ? currentPackage : null,
      amountDiscount: amount,
      totalPrice:
        _.get(planInfo, 'originalPrice', 0) -
        amount +
        (_.get(planInfo, 'isExtraFeeCharged', false) ? _.get(planInfo, 'extraFee', 0) : 0)
    };
  }

  return chargeInfo;
};

/**
 * Build addon show in subscription page
 * @param {*} allAddon
 * @param {*} statusStore
 * @returns
 */
export const buildAddonAdded = (allAddon, statusStore) => {
  const { addOnTrials = [], subscribedAddOnsInfos = [] } = allAddon;
  return subscribedAddOnsInfos.map(addon => {
    let isTrialPeriod = false;
    const addonName = _.get(addon, 'addOnName', null);
    const findAddon = addOnTrials.find(e => e.addOnName === addonName);
    const trialEndDate = _.get(findAddon, 'trialEndDate', 0);
    const addonRemainingCycles = _.get(findAddon, 'remainingDays', 0);
    if (findAddon) {
      isTrialPeriod = addonRemainingCycles > 0;
    }
    return {
      ...addon,
      trialEndDate,
      isTrialPeriod: ['trial', 'expiredSoon', 'expired'].includes(statusStore) || isTrialPeriod
    };
  });
};

// Check if Shopify plan is a dev store or test plan
export const checkShopifyTestPlan = (planName: string, tenantId: string): boolean => {
  return (
    [
      'affiliate',
      'cancelled',
      'staff',
      'closed',
      'plus_partner_sandbox',
      'trial',
      'frozen',
      'paused',
      'fraudulent',
      'partner_test',
      'dormant'
    ].includes(planName) && !tenantId.includes('bc-dev-v2')
  );
};

export const checkSubscribedPackageV3 = (chargeActiveInfo: object): boolean => {
  const chargeInfoActiveName = _.get(chargeActiveInfo, 'name', '');
  const chargeInfoActiveNameSplit = chargeInfoActiveName.split('_');

  // Check if store subscribed subscription v3
  return ['basic', 'essential', 'professional'].includes(
    _.get(chargeInfoActiveNameSplit, '[0]', '')
  );
};

export const checkUsingVariantForFree = (tenantApp: Record<string, any>): boolean => {
  const { tenantId, appParams = {}, recentInstalledAt, settings = {} } = tenantApp;

  // These stores required to use variant-display for free => Not lock
  const usingVariantForFreeStores = [
    'stg-official-mikihouse.myshopify.com',
    'jnk-ireland.myshopify.com',
    'hold-end-dist.myshopify.com',
    'bc-dev-v2-anna16.myshopify.com' // for test
  ];
  if (usingVariantForFreeStores.includes(tenantId)) return true;

  // Check use variant-display feature
  const useVariantFeature = _.get(settings, 'variantsDisplay.settings.variant-display', false);
  if (!useVariantFeature) return false;

  const totalProduct = _.get(appParams, 'currentTotalProducts', 0);

  // if over 5k product => need to buy addon
  if (totalProduct > 5000) return false;

  // Check new user, installing app after 2023-04-19
  const isNewUser = checkUserV3(recentInstalledAt);
  // if user is after release => need to buy addon
  if (isNewUser) return false;

  return true;
};

/**
 * Check new user, installing app after 2023-04-19
 * @param {number} recentInstalledAt
 * @returns
 */
export const checkUserV3 = (recentInstalledAt: number): boolean => {
  const releaseTime = moment(RELEASE_0423_DAY).startOf('day');
  const recentInstalledTime = moment(recentInstalledAt * 1000);
  return recentInstalledTime.isAfter(releaseTime);
};

/**
 * Allow to subscribe special discount code BFCM20
 *
 * @param  {object} tenantApp
 * @param  {string} subscribeStatus
 *
 * @return {boolean}
 */
export const enableNewPricingPageExceededUser = (tenantApp: Object) => {
  const subscriptionStatus = getSubscriptionStatus(tenantApp);
  const status = _.get(subscriptionStatus, 'status', '');

  return status === 'exceeded';
};

export const checkUsePersonalization = (tenantApp: Record<string, any>) => {
  const usePersonalized = _.get(tenantApp, 'settings.personalization.settings.enable') || false;

  if (usePersonalized) {
    return true;
  }

  return false;
};

/**
 * Check to show new pricing page v4
 * @param {*} tenantApp
 * @returns
 */
export const enableSubscriptionV4 = (tenantApp: Record<string, any>) => {
  return _.get(tenantApp, 'settings.subscriptionV4.settings.enable', false);
};

export const isSpecialNotifyPriceChangeStore = (tenantId: string, invoice): boolean => {
  const extraFeeStatus = _.get(invoice, 'extraFeeStatus', '');
  if (extraFeeStatus === 'cancelled') {
    return false;
  }
  return SPECIAL_PRICING_CHANGE_STORES.includes(tenantId);
};

// For pricing change store, EXCEPT special notify store, will auto price change and upgrade to gmv or has extra fee in invoice
export const isPricingChangeStore = (tenantApp: Record<string, any>, invoice: any): boolean => {
  const tenantId = _.get(tenantApp, 'tenantId', '');
  const extraFeeStatus = _.get(invoice, 'extraFeeStatus', '');
  if (extraFeeStatus === 'cancelled') {
    return false;
  }
  const hasExtraFee = _.has(invoice, 'extraFee') && invoice.extraFee > 0;

  // if store has extra fee in invoice but in special notify list => not auto price change, just show normal plan page with notify
  if (SPECIAL_PRICING_CHANGE_STORES.includes(tenantId)) {
    return false;
  }

  // general stores and white glove stores will auto price change and upgrade to gmv or has extra fee in invoice
  return (
    hasExtraFee ||
    GENERAL_NOTIFY_PRICE_CHANGE_STORES.includes(tenantId) ||
    WHITE_GLOVE_TENANT_IDS.includes(tenantId)
  );
};

// All store in pricing change list includes
// 1. General stores
// 2. White glove stores
// 3. Special + General  stores
// 4. Special + White glove  stores
// => all list with show banner in homepage
export const isInAllListPricingStore = (tenantApp: Record<string, any>, invoice: any): boolean => {
  const tenantId = _.get(tenantApp, 'tenantId', '');
  const hasExtraFee = _.has(invoice, 'extraFee') && invoice.extraFee > 0;

  // general stores and white glove stores will auto price change and upgrade to gmv or has extra fee in invoice
  return (
    hasExtraFee ||
    GENERAL_NOTIFY_PRICE_CHANGE_STORES.includes(tenantId) ||
    WHITE_GLOVE_TENANT_IDS.includes(tenantId) ||
    WHITE_GLOVE_SPECIAL_TENANT_IDS.includes(tenantId) ||
    GENERAL_SPECIAL_PRICING_CHANGE_STORE.includes(tenantId)
  );
};

export const isGMVMigrationStore = (
  tenantApp: Record<string, any>,
  migrationStores: Array<string> = []
): boolean => {
  const tenantId = _.get(tenantApp, 'tenantId', '');

  const isSubscriptionV5 = _.get(tenantApp, 'settings.subscriptionV5.settings.enable', false);
  return migrationStores.includes(tenantId) && !isSubscriptionV5;
};

const LEGACY_GMV_PLAN_PREFIXES = ['plan', 'regular', 'plus'];

const GMV_PLAN_NAME_MAP_V4 = {
  basic: {
    launch: 'BasicV4ToLaunch',
    convert: 'BasicV4ToConvert',
    accelerate: 'BasicV4ToAccelerate'
  },
  essential: {
    convert: 'EssentialV4ToConvert',
    accelerate: 'EssentialV4ToAccelerate'
  }
};

const GMV_PLAN_NAME_MAP_V3 = {
  basic: {
    launch: 'BasicV3ToLaunch',
    convert: 'BasicV3ToConvert',
    accelerate: 'BasicV3ToAccelerate'
  },
  essential: {
    convert: 'EssentialV3ToConvert',
    accelerate: 'EssentialV3ToAccelerate'
  }
};

export const getGMVMigrationPlanName = (
  tenantApp: Record<string, any>,
  gmvPlan: string
): string => {
  let selectedPlan = _.get(tenantApp, 'selectedPlan', '');
  if (!selectedPlan) return '';

  selectedPlan = convertOldPlanName(selectedPlan);

  let targetPlanType = '';
  if (gmvPlan.startsWith('launch')) targetPlanType = 'convert';
  else if (gmvPlan.startsWith('convert')) targetPlanType = 'convert';
  else if (gmvPlan.startsWith('accelerate')) targetPlanType = 'accelerate';

  if (!targetPlanType) return '';

  const GMV_PLAN_NAME_MAP = enableSubscriptionV4(tenantApp)
    ? GMV_PLAN_NAME_MAP_V4
    : GMV_PLAN_NAME_MAP_V3;

  const sourcePlanType = Object.keys(GMV_PLAN_NAME_MAP).find(prefix =>
    selectedPlan.startsWith(prefix)
  );
  if (!sourcePlanType) return '';

  return _.get(GMV_PLAN_NAME_MAP, `${sourcePlanType}.${targetPlanType}`, '');
};

export const convertOldPlanName = (selectedPlan = '') => {
  if (typeof selectedPlan !== 'string') {
    return '';
  }

  const loverCaseSelectedPlan = selectedPlan.toLocaleLowerCase();
  if (LEGACY_GMV_PLAN_PREFIXES.some(prefix => loverCaseSelectedPlan.startsWith(prefix))) {
    return 'basic';
  }

  return selectedPlan;
};

const SUBSCRIPTION_PACKAGE_PREFIXES = ['basic', 'essential', 'professional', 'freemium'];

const getPackagePrefixFromPlanId = (planId: string = '') => {
  const prefix = planId.split('_')[0];
  return SUBSCRIPTION_PACKAGE_PREFIXES.includes(prefix) ? prefix : '';
};

const findPackagePlanByName = (packagePlans: Object = {}, planId: string = '') => {
  if (!planId) return null;

  return Object.values(packagePlans)
    .reduce((result, plans) => result.concat(plans || []), [])
    .find(plan => plan && plan.name === planId);
};

export const getPricingUpdateSubscribePlanId = (
  tenantApp: Object = {},
  invoice: Object = {},
  correctPlan: Object = {},
  packagePlans: Object = {}
) => {
  const selectedPlan = _.get(tenantApp, 'selectedPlan', '');
  const chargePlanId = _.get(invoice, 'chargeInfo.name', '');
  const correctPlanId = _.get(correctPlan, 'name', '');
  const candidates = [chargePlanId, selectedPlan, correctPlanId].filter(Boolean);

  const exactPlanId = candidates.find(planId => findPackagePlanByName(packagePlans, planId));
  if (exactPlanId) return exactPlanId;

  const packagePrefix =
    getPackagePrefixFromPlanId(selectedPlan) ||
    getPackagePrefixFromPlanId(chargePlanId) ||
    getPackagePrefixFromPlanId(correctPlanId) ||
    'basic';
  const packagePlanList = _.get(packagePlans, packagePrefix, []);
  const currentPrice = Number(_.get(invoice, 'chargeInfo.price', _.get(correctPlan, 'price', 0)));
  const currentProducts = _.get(tenantApp, 'appParams.currentTotalProducts', 0);

  const planByPrice = packagePlanList.find(plan => Number(plan.price) === currentPrice);
  if (planByPrice) return planByPrice.name;

  const planByProducts = packagePlanList.find(
    plan => currentProducts && plan.products >= currentProducts
  );
  if (planByProducts) return planByProducts.name;

  if (selectedPlan.includes('_0_5')) {
    return selectedPlan.replace('_0_5', '_2_5');
  }

  return selectedPlan || chargePlanId || correctPlanId;
};
